import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as Toast from '@radix-ui/react-toast';
import { useTranslation } from 'react-i18next';
import { useSignalRConnection } from '../../hooks/useSignalRConnection';
import Icon from '../AppIcon';

interface OtpData {
  otp: string;
  purpose: string;
  contextTitle: string;
  expiresAt: string;
}

const OtpToast: React.FC = () => {
  const { t } = useTranslation();
  const { subscribe } = useSignalRConnection();
  const [otpData, setOtpData] = useState<OtpData | null>(null);
  const [open, setOpen] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    const unsub = subscribe('OtpReceived', (data: OtpData) => {
      setOtpData(data);
      setOpen(true);
      setCopied(false);

      const expiresAt = new Date(data.expiresAt).getTime();
      const updateRemaining = () => {
        const left = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
        setRemainingSeconds(left);
        if (left <= 0) {
          clearInterval(timerRef.current);
          setOpen(false);
        }
      };
      updateRemaining();
      clearInterval(timerRef.current);
      timerRef.current = setInterval(updateRemaining, 1000);
    });

    return () => {
      unsub();
      clearInterval(timerRef.current);
    };
  }, [subscribe]);

  const handleCopy = useCallback(() => {
    if (otpData?.otp) {
      navigator.clipboard.writeText(otpData.otp).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {
        // Silent fail — clipboard not available
      });
    }
  }, [otpData]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <Toast.Provider swipeDirection="right" duration={Infinity}>
      <Toast.Root
        className="bg-card border border-border rounded-xl shadow-lg p-4 w-[360px] data-[state=open]:animate-in data-[state=open]:slide-in-from-top-full data-[state=closed]:animate-out data-[state=closed]:fade-out"
        open={open}
        onOpenChange={setOpen}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="KeyRound" size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <Toast.Title className="text-sm font-semibold text-foreground">
              {t('notifications.otpReceived.title', 'Verification Code')}
            </Toast.Title>
            <Toast.Description className="text-xs text-muted-foreground mt-0.5">
              {otpData?.contextTitle}
            </Toast.Description>

            <div className="mt-3 flex items-center gap-2">
              <div className="bg-muted rounded-lg px-4 py-2 flex-1 text-center">
                <span className="text-2xl font-bold tracking-[0.3em] font-mono text-foreground">
                  {otpData?.otp}
                </span>
              </div>
              <button
                onClick={handleCopy}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
                title={t('common.copy', 'Copy')}
              >
                <Icon name={copied ? 'Check' : 'Copy'} size={18} className={copied ? 'text-green-500' : 'text-muted-foreground'} />
              </button>
            </div>

            <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Icon name="Clock" size={12} />
              <span>{t('notifications.otpReceived.expiresIn', 'Expires in')} {formatTime(remainingSeconds)}</span>
            </div>
          </div>

          <Toast.Close className="p-1 rounded hover:bg-muted transition-colors">
            <Icon name="X" size={14} className="text-muted-foreground" />
          </Toast.Close>
        </div>
      </Toast.Root>

      <Toast.Viewport className="fixed top-4 end-4 z-[100] flex flex-col gap-2 w-[360px] max-w-[100vw] outline-none" />
    </Toast.Provider>
  );
};

export default OtpToast;
